import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { contentAPI, adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import ImageUpload from '../components/ImageUpload';
import AdminBackLink from '../components/AdminBackLink.jsx';
import { PageHeader } from '../components/ui';
import './ModuleAdmin.css';

const emptySection = () => ({ title: '', trekIds: [] });

export default function ExpeditionPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [mainTitle, setMainTitle] = useState('Expeditions');
  const [heroSubtitle, setHeroSubtitle] = useState('');
  const [heroImage, setHeroImage] = useState('');
  const [sections, setSections] = useState([emptySection()]);
  const [openDropupIndex, setOpenDropupIndex] = useState(null);
  const dropdownRefs = useRef({});

  const { data: sectionRes, isLoading } = useQuery(
    ['expedition-section'],
    () => contentAPI.getExpeditionSection(),
    { staleTime: 60000 }
  );
  const { data: treksRes } = useQuery(
    ['treks-with-upcoming-batches'],
    () => contentAPI.getTreksWithUpcomingBatches()
  );
  const treks = treksRes?.data?.data || [];

  useEffect(() => {
    const d = sectionRes?.data?.data;
    if (!d) return;
    setMainTitle(d.mainTitle ?? 'Expeditions');
    setHeroSubtitle(String(d.heroSubtitle ?? '').trim());
    setHeroImage(String(d.heroImage ?? '').trim());
    if (Array.isArray(d.sections) && d.sections.length > 0) {
      setSections(
        d.sections.map((sec) => {
          const fromIds = Array.isArray(sec.trekIds) ? sec.trekIds.map((id) => (typeof id === 'number' ? id : Number(id))).filter((n) => Number.isFinite(n) && n > 0) : [];
          const fromTreks = Array.isArray(sec.treks) ? sec.treks.map((t) => t.id).filter(Boolean) : [];
          const trekIds = fromIds.length > 0 ? fromIds : fromTreks;
          return { title: sec.title ?? '', trekIds };
        })
      );
    } else if (d.title != null || d.subtitle != null || (Array.isArray(d.trekIds) && d.trekIds.length)) {
      setMainTitle(d.title ?? d.mainTitle ?? 'Expeditions');
      setSections([{ title: d.subtitle ?? 'Expeditions', trekIds: Array.isArray(d.trekIds) ? d.trekIds : (d.treks || []).map((t) => t.id) }]);
    }
  }, [sectionRes?.data?.data]);

  const updateMutation = useMutation(
    (payload) => contentAPI.updateExpeditionSection(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expedition-section']);
        showToast({ message: 'Expedition page saved.', severity: 'success' });
      },
      onError: (err) => {
        showToast({ message: err?.response?.data?.message || 'Failed to save.', severity: 'error' });
      }
    }
  );


  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      mainTitle: mainTitle.trim(),
      heroSubtitle: heroSubtitle.trim(),
      heroImage: heroImage.trim(),
      sections: sections.map((s) => ({
        title: s.title ?? '',
        trekIds: (s.trekIds || []).map((id) => (typeof id === 'number' ? id : Number(id))).filter((n) => Number.isFinite(n) && n > 0)
      }))
    };
    updateMutation.mutate(payload);
  };

  const setSection = (idx, patch) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const setSectionTrekIds = (idx, trekIds) => setSection(idx, { trekIds });
  const toggleTrek = (sectionIdx, trekId) => {
    const id = typeof trekId === 'number' ? trekId : Number(trekId);
    if (!Number.isFinite(id) || id <= 0) return;
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== sectionIdx) return s;
        const ids = (s.trekIds || []).map((x) => (typeof x === 'number' ? x : Number(x))).filter(Number.isFinite);
        const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
        return { ...s, trekIds: next };
      })
    );
  };

  const addSection = () => setSections((prev) => [...prev, emptySection()]);
  const removeSection = (idx) => setSections((prev) => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    if (openDropupIndex == null) return;
    const ref = dropdownRefs.current[openDropupIndex];
    const handleClickOutside = (e) => {
      if (ref && !ref.contains(e.target)) setOpenDropupIndex(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropupIndex]);

  return (
    <div className="module-page">
      <AdminBackLink to="/expedition" label="Back to expeditions" />
      <PageHeader
        title="Expedition Page"
        subtitle="Hero matches the public Special Events page (same style as Blogs). Add sections and treks below."
      />

      {isLoading ? (
        <Card>
          <CardContent>
            <div className="ops-loading-box">
              <CircularProgress size={28} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">Loading expedition settings…</Typography>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card component="form" className="expedition-page-form" onSubmit={handleSubmit}>
          <CardContent>
          <div className="trek-editor-section">
            <label className="trek-editor-section-title">Main title (hero heading)</label>
            <input
              type="text"
              value={mainTitle}
              onChange={(e) => setMainTitle(e.target.value)}
              placeholder="e.g. Special Events"
              className="trek-similar-dropdown-btn"
              style={{ marginBottom: 0 }}
            />
          </div>

          <div className="trek-editor-section">
            <label className="trek-editor-section-title">Hero subtitle</label>
            <input
              type="text"
              value={heroSubtitle}
              onChange={(e) => setHeroSubtitle(e.target.value)}
              placeholder="e.g. Unique experiences curated for the bold"
              className="trek-similar-dropdown-btn"
              style={{ marginBottom: 0 }}
            />
            <p className="trek-editor-hint">Shown under the title. If empty, the site may still use the Home “Special Events subtitle” from Site copy.</p>
          </div>

          <div className="trek-editor-section">
            <ImageUpload
              label="Hero background image (optional)"
              value={heroImage}
              onChange={setHeroImage}
              uploadImage={async (file) => {
                const res = await adminAPI.uploadImage(file);
                return res?.data?.data?.url || res?.data?.url || '';
              }}
            />
            <p className="trek-editor-hint">Same behavior as Blogs: photo with dark gradient overlay. Leave empty for solid orange hero.</p>
          </div>

          {sections.map((sec, idx) => (
            <div key={idx} className="expedition-section-block">
              <div className="trek-editor-section">
                <label className="trek-editor-section-title">Section title</label>
                <div className="expedition-section-title-row">
                  <input
                    type="text"
                    value={sec.title}
                    onChange={(e) => setSection(idx, { title: e.target.value })}
                    placeholder="e.g. Mountain Expeditions"
                    className="trek-similar-dropdown-btn"
                    style={{ flex: 1, marginBottom: 0 }}
                  />
                  <Button type="button" variant="outlined" color="error" className="expedition-remove-section" onClick={() => removeSection(idx)}>
                    Remove section
                  </Button>
                </div>
                <p className="trek-editor-hint">Select treks to show in this section. Order preserved.</p>
                <div
                  className="trek-similar-dropdown-wrap"
                  ref={(el) => { dropdownRefs.current[idx] = el; }}
                >
                  <button
                    type="button"
                    className="trek-similar-dropdown-btn"
                    onClick={() => setOpenDropupIndex((prev) => (prev === idx ? null : idx))}
                    aria-expanded={openDropupIndex === idx}
                  >
                    {(sec.trekIds || []).length > 0
                      ? `${(sec.trekIds || []).length} trek${(sec.trekIds || []).length !== 1 ? 's' : ''} selected`
                      : 'Select treks…'}
                    <span className="trek-similar-dropdown-arrow" aria-hidden>{openDropupIndex === idx ? ' ▲' : ' ▼'}</span>
                  </button>
                  {openDropupIndex === idx && (
                    <div className="trek-similar-dropdown-panel" role="listbox">
                      {treks.length === 0 ? (
                        <span className="trek-editor-hint">No treks with upcoming batches.</span>
                      ) : (
                        treks.map((t) => {
                          const tid = typeof t.id === 'number' ? t.id : Number(t.id);
                          const checked = (sec.trekIds || []).some((id) => Number(id) === tid);
                          return (
                            <label key={t.id} className="trek-similar-check">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleTrek(idx, t.id)}
                              />
                              <span>{t.name}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          <Button type="button" variant="outlined" className="expedition-add-section" onClick={addSection} sx={{ mt: 1 }}>
            Add section
          </Button>

          <Box className="expedition-page-actions" sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={updateMutation.isLoading}>
              {updateMutation.isLoading ? 'Saving…' : 'Save'}
            </Button>
          </Box>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
